import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, User, PiggyBank } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { updateTransaction, getFamily } from '../lib/api';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'logement', emoji: '🏠' },
  { id: 'transport', emoji: '🚗' },
  { id: 'alimentation', emoji: '🍔' },
  { id: 'abonnements', emoji: '📱' },
  { id: 'shopping', emoji: '🛍️' },
  { id: 'loisirs', emoji: '🎮' },
  { id: 'sante', emoji: '💊' },
  { id: 'epargne', emoji: '💰' },
  { id: 'education', emoji: '📚' },
  { id: 'animaux', emoji: '🐕' },
  { id: 'enfants', emoji: '👶' },
  { id: 'cadeaux', emoji: '🎁' },
  { id: 'entretien', emoji: '🔧' },
  { id: 'professionnel', emoji: '💼' },
  { id: 'divers', emoji: '📦' },
];

const PAYMENT_METHODS = [
  { id: 'card', name_fr: 'Carte', name_en: 'Card', name_pt: 'Cartão', emoji: '💳' },
  { id: 'cash', name_fr: 'Espèces', name_en: 'Cash', name_pt: 'Dinheiro', emoji: '💵' },
  { id: 'transfer', name_fr: 'Virement', name_en: 'Transfer', name_pt: 'Transferência', emoji: '🏦' },
  { id: 'check', name_fr: 'Chèque', name_en: 'Check', name_pt: 'Cheque', emoji: '📝' },
  { id: 'ticket_restaurant', name_fr: 'Ticket resto', name_en: 'Meal voucher', name_pt: 'Vale refeição', emoji: '🍽️' },
  { id: 'cheque_vacances', name_fr: 'Chèque vacances', name_en: 'Holiday voucher', name_pt: 'Vale férias', emoji: '🏖️' },
  { id: 'cheque_cadeau', name_fr: 'Chèque cadeau', name_en: 'Gift card', name_pt: 'Vale presente', emoji: '🎁' },
  { id: 'other', name_fr: 'Autre', name_en: 'Other', name_pt: 'Outro', emoji: '📋' },
];

export default function EditTransactionModal({ transaction, open, onClose, onSuccess }) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [family, setFamily] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    description: '',
    date: '',
    payment_method: 'card',
    budget_type: 'personal',
  });

  // Load family data to check budget mode
  useEffect(() => {
    if (open && user?.family_id) {
      getFamily()
        .then(res => setFamily(res.data.family))
        .catch(err => console.error('Failed to load family:', err));
    }
  }, [open, user?.family_id]);

  // Initialize form when transaction changes
  useEffect(() => {
    if (transaction) {
      setFormData({
        amount: transaction.amount?.toString() || '',
        category: transaction.category || '',
        description: transaction.description || '',
        date: transaction.date ? transaction.date.split('T')[0] : new Date().toISOString().split('T')[0],
        payment_method: transaction.payment_method || 'card',
        budget_type: transaction.budget_type || 'personal',
      });
    }
  }, [transaction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('invalid_amount'));
      return;
    }

    if (!formData.category) {
      toast.error(language === 'fr' ? 'Sélectionnez une catégorie' : language === 'pt' ? 'Selecione uma categoria' : 'Select a category');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData = {
        amount: parseFloat(formData.amount),
        category: formData.category,
        description: formData.description,
        date: new Date(formData.date).toISOString(),
        payment_method: formData.payment_method,
      };
      
      // Include budget_type only for hybrid mode families
      if (family?.budget_mode === 'hybrid') {
        updateData.budget_type = formData.budget_type;
      }
      
      await updateTransaction(transaction.transaction_id, updateData);
      
      toast.success(t('transaction_updated'));
      onSuccess?.();
    } catch (error) {
      console.error('Failed to update transaction:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la modification' : language === 'pt' ? 'Erro ao atualizar transação' : 'Error updating transaction'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-4 mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">✏️</span>
            {language === 'fr' ? 'Modifier la transaction' : language === 'pt' ? 'Editar transação' : 'Edit Transaction'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4 overflow-hidden">
          {/* Amount */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('amount')}
            </label>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                className="text-xl font-bold pr-12"
                data-testid="edit-amount"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                €
              </span>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('category')}
            </label>
            {/* Horizontal slider for categories */}
            <div 
              className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                  className={`flex-none w-14 p-2 rounded-xl border-2 transition-all ${
                    formData.category === cat.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`edit-category-${cat.id}`}
                >
                  <span className="text-lg block text-center">{cat.emoji}</span>
                  <span className="text-[8px] text-center block mt-1 truncate">
                    {t(`cat_${cat.id}`)}
                  </span>
                </button>
              ))}
              <div className="flex-none w-2" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('description')}
            </label>
            <Input
              placeholder={t('example_grocery')}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              data-testid="edit-description"
            />
          </div>

          {/* Date */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {language === 'fr' ? 'Date' : 'Date'}
            </label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              data-testid="edit-date"
            />
          </div>

          {/* Payment Method - Horizontal slider */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {language === 'fr' ? 'Moyen de paiement' : language === 'pt' ? 'Método de pagamento' : 'Payment Method'}
            </label>
            <div 
              className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: method.id }))}
                  className={`flex-none w-14 p-2 rounded-xl border-2 transition-all ${
                    formData.payment_method === method.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`edit-payment-${method.id}`}
                >
                  <span className="text-lg block text-center">{method.emoji}</span>
                  <span className="text-[8px] block text-center mt-1 truncate">
                    {language === 'fr' ? method.name_fr : language === 'pt' ? method.name_pt : method.name_en}
                  </span>
                </button>
              ))}
              <div className="flex-none w-2" />
            </div>
          </div>

          {/* Budget Type (for hybrid mode) */}
          {family && family.budget_mode === 'hybrid' && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('budget_type')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, budget_type: 'personal' }))}
                  className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.budget_type === 'personal'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-border hover:border-blue-300'
                  }`}
                  data-testid="edit-budget-type-personal"
                >
                  <User className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">
                    {t('personal')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, budget_type: 'common' }))}
                  className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    formData.budget_type === 'common'
                      ? 'border-green-500 bg-green-50'
                      : 'border-border hover:border-green-300'
                  }`}
                  data-testid="edit-budget-type-common"
                >
                  <PiggyBank className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium">
                    {t('common_pot')}
                  </span>
                </button>
              </div>
            </div>
          )}
        </form>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto">
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
